/**
 * The palette of body primitives an admin can compose a runner step from — the admin-side, declarative
 * mirror of the guided runner's primitive registry (`Campaigns/guided/composition/primitives/core.tsx`,
 * enumerated by `registeredPrimitiveKeys()`). Kept here as static UI metadata (like `CATEGORY_SCHEMAS`
 * and `STEP_KINDS`) rather than imported from the runner subtree, so the admin bundle never pulls in the
 * lazy runner primitive components just to know they exist. The *data* a primitive renders is still
 * entirely DB-driven — this only describes which primitives the code can render and which config keys
 * each one reads. Keep this list in sync with `core.tsx` when a primitive is added or removed.
 *
 * The composer writes these into a `step_layout` `CampaignContentBlock` payload as
 * `primitives: Array<{ key, config? }>`, which `resolveStepLayout` reads to build the step body. Every
 * config field is optional; a blank value falls back to the primitive's bundled default / section
 * heading, so authoring config is purely additive.
 */

export type PrimitiveFieldType = 'text' | 'multiline' | 'boolean';

export interface PrimitiveField {
  /** The config key written into the primitive's `config` object. */
  key: string;
  label: string;
  type: PrimitiveFieldType;
  help?: string;
}

export interface PrimitiveDef {
  /** The registered primitive key the runner resolves (`getPrimitive(key)`). */
  key: string;
  label: string;
  description: string;
  /** The config keys this primitive reads (all optional). */
  fields: PrimitiveField[];
}

const TITLE: PrimitiveField = {
  key: 'title',
  label: 'Title',
  type: 'text',
  help: 'Overrides the section heading. Blank uses the heading set under Step content.',
};
const LEAD: PrimitiveField = {
  key: 'lead',
  label: 'Lead',
  type: 'multiline',
  help: 'Intro line shown under the heading.',
};
const ICON: PrimitiveField = {
  key: 'icon',
  label: 'Icon',
  type: 'text',
  help: 'Optional icon name (e.g. check, people, chat, deck, table, search).',
};

export const STEP_LAYOUT_PRIMITIVES: PrimitiveDef[] = [
  {
    key: 'stakeholder-roster',
    label: 'Stakeholder roster',
    description: 'The customer-side personas to get in the room, by priority.',
    fields: [TITLE, LEAD, ICON],
  },
  {
    key: 'connect-tools',
    label: 'Connect tools',
    description: 'Tool-connection cards used during customer profiling.',
    fields: [TITLE, ICON],
  },
  {
    key: 'conversation-guide',
    label: 'Conversation guide',
    description: 'The guided talk-track for the proposal conversation.',
    fields: [
      TITLE,
      LEAD,
      ICON,
      { key: 'guideTitle', label: 'Guide title', type: 'text' },
      { key: 'guideBody', label: 'Guide body', type: 'multiline' },
    ],
  },
  {
    key: 'discovery-questions',
    label: 'Discovery questions',
    description: 'The discovery question set surfaced for the conversation.',
    fields: [TITLE, LEAD, ICON],
  },
  {
    key: 'session-deck',
    label: 'Session deck',
    description: 'The workshop presentation deck viewer.',
    fields: [TITLE, LEAD],
  },
  {
    key: 'comments',
    label: 'Comments',
    description: 'The collaborative comments panel.',
    fields: [
      {
        key: 'hideWhenPresenting',
        label: 'Hide while presenting',
        type: 'boolean',
        help: 'Hide the comments panel when the workshop is in presentation mode.',
      },
    ],
  },
  {
    key: 'deep-dive-panel',
    label: 'Deep-dive panel',
    description: 'The interactive deep-dive scenario console.',
    fields: [TITLE, LEAD, ICON],
  },
  {
    key: 'gap-analysis',
    label: 'Gap analysis',
    description: 'The gap-analysis table for the recommendation.',
    fields: [TITLE, LEAD, ICON],
  },
  {
    key: 'report-editor',
    label: 'Report editor',
    description: 'The recommendation report editor (hidden while presenting).',
    fields: [
      TITLE,
      LEAD,
      ICON,
      {
        key: 'exampleNotice',
        label: 'Example-content notice',
        type: 'multiline',
        help: 'Banner shown above the report editor, reminding the SE to replace example content before sharing. Blank uses the default wording.',
      },
    ],
  },
];

const BY_KEY: Record<string, PrimitiveDef> = STEP_LAYOUT_PRIMITIVES.reduce(
  (acc, def) => {
    acc[def.key] = def;
    return acc;
  },
  {} as Record<string, PrimitiveDef>
);

/** The catalog entry for a primitive key, or undefined for an unknown/ad-hoc key. */
export const primitiveDef = (key: string): PrimitiveDef | undefined => BY_KEY[key];

/** A friendly label for a primitive key, falling back to the raw key when it isn't in the catalog. */
export const primitiveLabel = (key: string): string => BY_KEY[key]?.label ?? key;
